import React, { useState, useRef, useEffect } from "react"
import { createPortal } from "react-dom"
import PropTypes from "prop-types"
import { Button, Box } from "@mui/material"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"

const SidebarNavButton = ({
  icon,
  label,
  buttonVariant = "contained",
  onClick,
  link,
  linkNewTab,
  collapsed,
  subItems,
  dataAttribute,
  rightIcon,
}) => {
  const [open, setOpen] = useState(false)
  const btnRef = useRef(null)
  const menuRef = useRef(null)
  const [menuPos, setMenuPos] = useState({ left: 0, top: 0 })

  const handleToggle = () => setOpen(prev => !prev)
  const handleClose = () => setOpen(false)

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      const menuHeight = (subItems?.length || 1) * 48 + 16
      const viewportHeight = window.innerHeight
      let top = rect.top
      if (top + menuHeight > viewportHeight) {
        top = Math.max(8, viewportHeight - menuHeight - 8)
      }
      setMenuPos({ left: rect.right + 4, top })
    }
    if (open) {
      const handleClickOutside = event => {
        if (
          btnRef.current &&
          !btnRef.current.contains(event.target) &&
          menuRef.current &&
          !menuRef.current.contains(event.target)
        ) {
          setOpen(false)
        }
      }
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [open, subItems])

  const menu = subItems ? (
    <Box
      ref={menuRef}
      sx={{
        position: "fixed",
        left: menuPos.left,
        top: menuPos.top,
        bgcolor: "background.paper",
        boxShadow: 3,
        borderRadius: 2,
        minWidth: 180,
        zIndex: 2000,
        p: 1,
        display: open ? "flex" : "none",
        flexDirection: "column",
        gap: 1,
        maxHeight: "calc(100vh - 16px)",
        overflowY: "auto",
      }}
    >
      {subItems.map((sub, i) => {
        const handleSubClick = e => {
          if (sub.onClick) {
            sub.onClick(e)
            setOpen(false)
          } else if (sub.link) {
            if (sub.linkNewTab) {
              window.open(sub.link, "_blank", "noopener,noreferrer")
            } else {
              window.location.href = sub.link
            }
            setOpen(false)
          }
        }
        return (
          <Button
            key={i}
            {...(sub.dataAttribute ? { [sub.dataAttribute]: true } : {})}
            variant="text"
            color="inherit"
            sx={{
              px: 2,
              py: 1,
              minHeight: 40,
              cursor: sub.disabled ? "not-allowed" : "pointer",
              "&:hover": { bgcolor: sub.disabled ? undefined : "#f5f5f5" },
              opacity: sub.disabled ? 0.5 : 1,
              justifyContent: "flex-start",
              textTransform: "none",
              width: "100%",
              borderRadius: 0,
            }}
            onClick={sub.disabled ? undefined : handleSubClick}
            disabled={sub.disabled}
          >
            {sub.label}
          </Button>
        )
      })}
    </Box>
  ) : null

  const buttonProps = dataAttribute ? { [dataAttribute]: true } : {}

  const button = (
    <>
      <Button
        ref={btnRef}
        component="button"
        onClick={
          subItems
            ? handleToggle
            : e => {
                if (onClick) onClick(e)
              }
        }
        color="branded"
        variant={buttonVariant === "contained" ? "contained" : "contained"}
        sx={{
          minWidth: 45,
          bgcolor: buttonVariant === "contained" ? undefined : "transparent",
          "&:hover":
            buttonVariant === "contained"
              ? undefined
              : { backgroundColor: "#0000000a" },
          width: collapsed ? 20 : "100%",
          minHeight: 40,
          borderRadius: 2,
          justifyContent:
            buttonVariant === "contained" || collapsed
              ? "center"
              : "flex-start",
          gap: 1,
          position: "relative",
        }}
        {...buttonProps}
      >
        {icon}
        {!collapsed && (
          <Box
            sx={{
              fontWeight: 500,
              fontSize: 14,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "center",
              textTransform: "none",
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent:
                buttonVariant === "contained" ? "center" : "flex-start",
              gap: 1,
            }}
          >
            {label}
            {rightIcon && (
              <Box sx={{ ml: 1, display: "flex", alignItems: "center" }}>
                {rightIcon}
              </Box>
            )}
          </Box>
        )}
        {subItems && !collapsed && (
          <ChevronRightIcon sx={{ ml: 1, fontSize: 20, color: "#888" }} />
        )}
      </Button>
      {menu}
    </>
  )

  if (link) {
    return (
      <a
        href={link}
        target={linkNewTab ? "_blank" : undefined}
        rel={linkNewTab ? "noopener noreferrer" : undefined}
        style={{ textDecoration: "none", width: "100%" }}
      >
        {button}
      </a>
    )
  }
  return button
}

SidebarNavButton.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string,
  buttonVariant: PropTypes.string,
  onClick: PropTypes.func,
  link: PropTypes.string,
  linkNewTab: PropTypes.bool,
  collapsed: PropTypes.bool,
  dataAttribute: PropTypes.string,
  rightIcon: PropTypes.node,
  subItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      dataAttribute: PropTypes.string,
    }),
  ),
}

export default SidebarNavButton
